import EventTarget from "./eventTarget";
import { BlobServiceClient } from "@azure/storage-blob";

// This function is needed because Chrome doesn't accept a base64 encoded string
// as value for applicationServerKey in pushManager.subscribe yet
// https://bugs.chromium.org/p/chromium/issues/detail?id=802280
function urlBase64ToUint8Array(base64String) {
  var padding = '='.repeat((4 - base64String.length % 4) % 4);
  var base64 = (base64String + padding)
    .replace(/\-/g, '+') // eslint-disable-line no-useless-escape
    .replace(/_/g, '/');

  var rawData = window.atob(base64);
  var outputArray = new Uint8Array(rawData.length);

  for (var i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export class SubscriptionManager extends EventTarget {
  constructor({ token, subscriptionkey, stati, defaultAzureCredential }) {
      super();

      // Register a Service Worker.
      // navigator.serviceWorker.register('service-worker.js');
      this.token = token;
      this.subkey = subscriptionkey;
      this.dispatchEvent({ type: "init" });
      this.stati = stati || {};
      this.status = this.stati.LOADING;
      this.account = "analyticsdiag870";
      this.blob = "ContactChannel";
      this.container = "analyticsconfig";
      this.defaultAzureCredential = defaultAzureCredential;

      // SSR
      if(typeof navigator === "undefined") {
        return;
      }
      // When the Service Worker is ready, enable the UI (button),
      // and see if we already have a subscription set up.
      navigator.serviceWorker.ready
      .then(registration => {
        this.dispatchEvent({ type: "ready", subscriptions: [] });
        this.status = this.stati.READY;
        return registration.pushManager.getSubscription();
      }).then(subscription => {
        if (subscription) {
          this.subscription = subscription;
          // todo: fetch server-side subscription
          this.status = this.stati.SUBSCRIBED;
          this.dispatchEvent({ type: "subscribe", status: this.status, subscription });

        } else {
          this.status = this.stati.UNSUBSCRIBED;
          this.dispatchEvent({ type: "unsubscribe", status: this.status, subscription });
        }
      });
    }

    async getSubscription(defaultAzureCredential) {
      const blobServiceClient = new BlobServiceClient(
        `https://${this.account}.blob.core.windows.net`,
        defaultAzureCredential || this.defaultAzureCredential
      );
      const containerClient = blobServiceClient.getContainerClient(this.container);
      const blockBlobClient = containerClient.getBlockBlobClient(this.blob);
      const downloadBlockBlobResponse = await blockBlobClient.download(0);
      const blob = await downloadBlockBlobResponse.blobBody;
      const token = await blob.text();
      return token;
    }

    async configure({ token, subscriptionkey, defaultAzureCredential }) {
      this.token = token;
      this.subkey = subscriptionkey || await this.getSubscription(defaultAzureCredential || this.defaultAzureCredential);
      if(!this.token) {return;}
      if(this.token.then) {
        this.token = await this.token;
      }
      this.refresh();
    }

    async refresh() {
      const response = await fetch(`https://contactchannel.azure-api.net/contactchannel/contactChannel?subscription-key=${this.subkey}`, {
        method: 'get',
        headers: { authorization: "Bearer "+this.token }
      });
      const data = await response.json();
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription();
      const subscriptiondata = data.map((d) => ({ ...d, self: subscription && (subscription.endpoint === d.contact.endpoint) }));
      this.subscriptions = subscriptiondata;
      this.dispatchEvent({ type: "ready", status: this.status, subscriptions: this.subscriptions });
    }

    async subscribe() {
      const registration = await navigator.serviceWorker.ready;

      if(!this.token) {throw new Error(`Token required to subscribe!`);}
      if(this.token.then) {
        this.token = await this.token;
      }
      const response = await fetch(
        `https://contactchannel.azure-api.net/contactchannel/VapidPublicKey?subscription-key=${this.subkey}`,
        {headers: {authorization: "Bearer "+this.token}}
      );
      // Get the server's public key
      const vapidPublicKey = await response.text();
      // Chrome doesn't accept the base64-encoded (string) vapidPublicKey yet
      const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);

      if(!this.token) {throw new Error(`Token required to subscribe!`);}
      // Subscribe the user
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey
      });
      if(!this.token) {throw new Error(`Token required to subscribe!`);}
      await fetch(`https://contactchannel.azure-api.net/contactchannel/contactChannel?subscription-key=${this.subkey}`, {
        method: 'post',
        headers: {
          'Content-type': 'application/json',
          authorization: "Bearer "+this.token
        },
        body: JSON.stringify({
          contact: subscription,
          subscription: ["*"]
        })
      });
      this.subscription = subscription;
      this.status = this.stati.SUBSCRIBED;
      this.dispatchEvent({ type: "subscribe", status: this.status, subscription });
    }

    async unsubscribe() {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription();
      if(!this.token) {throw new Error(`Token required to subscribe!`);}
      const tasks = [
        subscription.unsubscribe(),
        fetch(`https://contactchannel.azure-api.net/contactChannel/contactChannel?subscription-key=${this.subkey}`, {
          method: 'delete',
          headers: {
            'Content-type': 'application/json',
            authorization: "Bearer "+this.token
          },
          body: JSON.stringify({
            contact: subscription
          })
        })
      ];
      try {
        await Promise.all(tasks);
        this.status = this.stati.UNSUBSCRIBED;
        this.dispatchEvent({ type: "unsubscribe", status: this.status, subscription });

      } catch(e) {
        this.status = this.stati.ERROR;
        this.dispatchEvent({ type: "error", status: this.status, subscription });
      }
    }

    async deleteSubscription(_subscription) {
      const registration = await navigator.serviceWorker.ready
      const subscription = _subscription || await registration.pushManager.getSubscription();
      if(!this.token) {throw new Error(`Token required to subscribe!`);}
      await fetch(`https://contactchannel.azure-api.net/contactChannel/contactChannel?subscription-key=${this.subkey}`, {
        method: 'delete',
        headers: {
          'Content-type': 'application/json',
          authorization: "Bearer "+this.token
        },
        body: JSON.stringify({
          contact: subscription
        })
      })
    }

}

export const SubscriptionManagerInstance = new SubscriptionManager({});
export default  SubscriptionManagerInstance;
